/**
 * Library module
 * Handles the Library tab (podcast list) functionality
 */

import { API } from '../core/api.js';
import { AppState } from '../core/state.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml, stripHtml } from '../core/utils.js';

// Pagination settings
const podcastsPerPage = 20;

/**
 * Update URL with podcast page number
 */
function updatePodcastsURL(page) {
    const params = new URLSearchParams();
    if (page > 0) params.set('page', page + 1);  // 1-indexed for users

    const newUrl = `#/podcasts${params.toString() ? '?' + params.toString() : ''}`;
    if (window.location.hash !== newUrl) {
        history.pushState({ page }, '', newUrl);
    }
}

/**
 * Handle URL parameters on load
 */
export function handlePodcastsPageFromURL(hash) {
    const match = hash.match(/#\/podcasts\??(.*)/);
    if (!match) {
        loadPodcasts(0);
        return;
    }

    const params = new URLSearchParams(match[1]);
    const page = Math.max(0, parseInt(params.get('page') || '1') - 1);
    loadPodcasts(page);
}

/**
 * Load podcasts list with pagination
 */
export async function loadPodcasts(page = 0) {
    try {
        AppState.currentPodcastPage = page;
        window.currentPodcastPage = page; // For global access

        // Update URL with current page
        updatePodcastsURL(page);

        const offset = page * podcastsPerPage;
        const data = await API.get(`/api/podcasts?limit=${podcastsPerPage}&offset=${offset}`);

        const podcasts = data.podcasts || [];
        const total = data.total || 0;
        const totalPages = Math.ceil(total / podcastsPerPage);

        const container = document.getElementById('podcasts');
        if (!container) return;

        // Create podcasts grid with pagination controls
        container.innerHTML = `
            <div id="podcast-grid-wrapper">
                <div id="podcast-grid"></div>
                ${totalPages > 1 ? `
                    <div class="pagination" style="margin-top: 2rem; display: flex; justify-content: center; gap: 0.5rem; align-items: center;">
                        <button class="btn btn-small ${page === 0 ? 'btn-disabled' : ''}"
                                onclick="loadPodcasts(${page - 1})"
                                ${page === 0 ? 'disabled' : ''}>
                            ← Previous
                        </button>
                        <span style="margin: 0 1rem;">
                            Page ${page + 1} of ${totalPages} (${total} podcasts)
                        </span>
                        <button class="btn btn-small ${page >= totalPages - 1 ? 'btn-disabled' : ''}"
                                onclick="loadPodcasts(${page + 1})"
                                ${page >= totalPages - 1 ? 'disabled' : ''}>
                            Next →
                        </button>
                    </div>
                ` : `<div style="text-align: center; margin-top: 1rem; color: var(--text-secondary);">${total} podcast${total !== 1 ? 's' : ''}</div>`}
            </div>
        `;

        const gridContainer = document.getElementById('podcast-grid');

        if (podcasts.length === 0 && page === 0) {
            gridContainer.innerHTML = '<p style="color: var(--text-secondary);">No podcasts in library. Add one to get started!</p>';
            return;
        }

        gridContainer.innerHTML = podcasts.map(podcast => `
            <div class="card">
                <div onclick="loadEpisodes(${podcast.id}, '${escapeHtml(podcast.title).replace(/'/g, "\\'")}')" style="cursor: pointer;">
                    <div class="card-header">
                        <div>
                            <div class="card-title">${escapeHtml(podcast.title)}</div>
                            ${podcast.category ? `<span class="card-category">${escapeHtml(podcast.category)}</span>` : ''}
                        </div>
                    </div>
                    <div class="card-description">${escapeHtml(stripHtml(podcast.description) || 'No description available')}</div>
                    <div class="card-meta">
                        <div class="card-stats">
                            <span class="card-stat">📝 ${podcast.episode_count} episodes</span>
                            ${podcast.author ? `<span class="card-stat">👤 ${escapeHtml(podcast.author)}</span>` : ''}
                        </div>
                        <div id="transcription-status-${podcast.id}" class="transcription-status-badge" style="margin-top: 0.5rem; font-size: 0.8rem;"></div>
                    </div>
                </div>
                <div class="card-actions">
                    <button class="btn btn-secondary card-action-secondary"
                            onclick="event.stopPropagation(); fetchPodcastHistory(${podcast.id}, '${escapeHtml(podcast.title).replace(/'/g, "\\'")}')"
                            title="Fetch more episodes from RSS feed">
                        📥 Fetch Episodes
                    </button>
                    <button class="btn btn-accent card-action-secondary"
                            onclick="event.stopPropagation(); submitBatchJob(${podcast.id}, 'transcribe', '${escapeHtml(podcast.title).replace(/'/g, "\\'")}')"
                            title="Transcribe and diarize all downloaded episodes">
                        🎙️ Process Audio
                    </button>
                    <button class="btn btn-accent card-action-secondary"
                            onclick="event.stopPropagation(); submitBatchJob(${podcast.id}, 'analyze', '${escapeHtml(podcast.title).replace(/'/g, "\\'")}')"
                            title="Run LLM analysis on all transcribed episodes">
                        🧠 Process Text
                    </button>
                    <button class="viz-trigger-btn"
                            onclick="event.stopPropagation(); showPodcastVisualizations(${podcast.id})"
                            title="View word cloud and analytics visualization">
                        ✨ Visualize
                    </button>
                    <button class="btn btn-primary card-action-primary"
                            onclick="event.stopPropagation(); loadEpisodes(${podcast.id}, '${escapeHtml(podcast.title).replace(/'/g, "\\'")}')">
                        View Episodes
                    </button>
                </div>
            </div>
        `).join('');

        // Fetch transcription status for each podcast (async, non-blocking)
        loadTranscriptionStatuses(podcasts.map(p => p.id));
    } catch (error) {
        console.error('Error loading podcasts:', error);
    }
}

/**
 * Load transcription status for multiple podcasts
 */
async function loadTranscriptionStatuses(podcastIds) {
    for (const podcastId of podcastIds) {
        try {
            const status = await API.get(`/api/podcasts/${podcastId}/transcription-status`);
            updateTranscriptionStatusBadge(podcastId, status);
        } catch (error) {
            console.error(`Error loading transcription status for podcast ${podcastId}:`, error);
        }
    }
}

/**
 * Update the transcription status badge for a podcast
 */
function updateTranscriptionStatusBadge(podcastId, status) {
    const badge = document.getElementById(`transcription-status-${podcastId}`);
    if (!badge) return;

    const { total_downloaded, with_transcription, missing_transcription, processing } = status;

    if (total_downloaded === 0) {
        badge.innerHTML = '<span style="color: var(--text-secondary);">No downloaded episodes</span>';
        return;
    }

    const parts = [];

    if (with_transcription > 0) {
        parts.push(`<span style="color: var(--success);">✓ ${with_transcription} transcribed</span>`);
    }

    if (missing_transcription > 0) {
        parts.push(`<span style="color: var(--warning);">⏳ ${missing_transcription} missing</span>`);
    }

    if (processing > 0) {
        parts.push(`<span style="color: var(--accent);">⚡ ${processing} processing</span>`);
    }

    badge.innerHTML = parts.join(' · ');
}

/**
 * Fetch podcast history (more episodes from RSS)
 */
export async function fetchPodcastHistory(podcastId, podcastTitle) {
    const pages = prompt(`Fetch more episodes for "${podcastTitle}"?\n\nEnter number of pages to fetch (each page has ~200 episodes):`, "5");

    if (!pages || isNaN(pages)) {
        return;
    }

    try {
        // Show loading indicator on the button
        const container = document.getElementById('podcast-grid');
        const cards = container.querySelectorAll('.card');
        cards.forEach(card => {
            if (card.innerHTML.includes(podcastTitle)) {
                const fetchBtn = card.querySelector('button');
                if (fetchBtn && fetchBtn.textContent.includes('Fetch More')) {
                    fetchBtn.disabled = true;
                    fetchBtn.textContent = '⏳ Fetching...';
                }
            }
        });

        const formData = new FormData();
        formData.append('pages', pages);

        const response = await fetch(`/api/podcasts/${podcastId}/fetch-history`, {
            method: 'POST',
            body: formData
        });

        const result = await response.json();

        if (response.ok) {
            alert(`✅ Success!\n\n${result.message}\n\nTotal found: ${result.total_episodes_found}\nNew added: ${result.new_episodes_added}\nDuplicates skipped: ${result.duplicates_skipped}`);
            loadPodcasts(AppState.currentPodcastPage);
        } else {
            alert(`❌ Error: ${result.detail || 'Failed to fetch episodes'}`);
            loadPodcasts(AppState.currentPodcastPage);
        }
    } catch (error) {
        console.error('Error fetching podcast history:', error);
        alert('❌ Failed to fetch podcast history');
        loadPodcasts(AppState.currentPodcastPage);
    }
}

/**
 * Submit a batch job for a podcast
 * @param {number} podcastId - Podcast ID
 * @param {string} jobType - 'transcribe', 'diarize', or 'analyze'
 * @param {string} podcastTitle - Podcast title for display
 */
export async function submitBatchJob(podcastId, jobType, podcastTitle) {
    const endpoints = {
        'transcribe': `/api/podcasts/${podcastId}/batch-transcribe`,
        'diarize': `/api/podcasts/${podcastId}/batch-diarize`,
        'analyze': `/api/podcasts/${podcastId}/batch-analyze`
    };

    const jobNames = {
        'transcribe': 'Transcription',
        'diarize': 'Diarization',
        'analyze': 'AI Analysis'
    };

    const endpoint = endpoints[jobType];
    const jobName = jobNames[jobType];

    if (!endpoint) {
        showNotification(`Unknown job type: ${jobType}`, 'error');
        return;
    }

    try {
        showNotification(`Submitting ${jobName} batch for "${podcastTitle}"...`, 'info');

        const response = await fetch(endpoint, {
            method: 'POST'
        });

        const result = await response.json();

        if (response.ok) {
            if (result.batch_id) {
                showNotification(
                    `${jobName} batch submitted: ${result.total} episodes queued`,
                    'success'
                );
            } else {
                showNotification(result.message || 'No episodes to process', 'info');
            }
        } else {
            showNotification(`Error: ${result.detail || 'Failed to submit batch'}`, 'error');
        }
    } catch (error) {
        console.error(`Error submitting ${jobType} batch:`, error);
        showNotification(`Failed to submit ${jobName} batch`, 'error');
    }
}

// Make submitBatchJob available globally for onclick handlers
window.submitBatchJob = submitBatchJob;
